import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, PlusCircle, Package, Menu, X, Store, Settings, LogOut, UserCircle, Layers } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const DashboardLayout = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // User details
  const userName = localStorage.getItem('userName') || 'Seller';
  const shopName = localStorage.getItem('shopName') || 'My Shop';
  const shopLogo = localStorage.getItem('shopLogo');

  // --- DYNAMIC FAVICON & TITLE LOGIC ---
  useEffect(() => {
    const currentPage = menuItems.find(item => item.path === location.pathname)?.name || "Dashboard";
    document.title = `${currentPage} | ${shopName}`;

    if (shopLogo) {
      const link = document.querySelector("link[rel~='icon']");
      if (link) link.href = shopLogo;
    }
  }, [location.pathname, shopName, shopLogo]);

  const menuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard size={20} /> },
    { name: 'Add Product', path: '/dashboard/add-product', icon: <PlusCircle size={20} /> },
    { name: 'Category Manager', path: '/dashboard/categories', icon: <Layers size={20} /> },
    { name: 'My Inventory', path: '/dashboard/my-products', icon: <Package size={20} /> },
    { name: 'Orders', path: '/dashboard/orders', icon: <Store size={20} /> },
    { name: 'Settings', path: '/dashboard/settings', icon: <Settings size={20} /> }
  ];

  const handleLogout = () => {
    toast((t) => (
      <div className="flex flex-col gap-3 p-1">
        <p className="font-bold text-gray-800 text-sm">Are you sure you want to logout?</p>
        <div className="flex gap-2">
          <button onClick={() => { toast.dismiss(t.id); performLogout(); }} className="bg-red-500 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg active:scale-95 transition-all">Yes, Logout</button>
          <button onClick={() => toast.dismiss(t.id)} className="bg-gray-100 text-gray-500 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all">Cancel</button>
        </div>
      </div>
    ), { duration: 5000, position: 'top-center' });
  };

  const performLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      <Toaster position="top-center" />
      
      {/* --- MOBILE TOP NAVBAR --- */}
      <div className="md:hidden bg-white border-b px-5 py-4 flex justify-between items-center sticky top-0 z-[110] shadow-sm w-full">
        <div className="flex items-center gap-2">
          <div className="bg-black p-1.5 rounded-lg text-white"><Store size={20} /></div>
          <h1 className="text-xl font-black text-gray-900 tracking-tight">{shopName}</h1>
        </div>
        <button onClick={() => setIsOpen(!isOpen)} className="p-2 rounded-xl bg-gray-50 text-gray-600">
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* --- FIXED SIDEBAR --- */}
      <aside className={`
        fixed inset-y-0 left-0 z-[100] w-72 bg-white border-r transform transition-transform duration-300 ease-in-out h-screen
        ${isOpen ? "translate-x-0" : "-translate-x-full"}
        md:translate-x-0
      `}>
        {/* Sidebar Header */}
        <div className="p-8 hidden md:block border-b mb-6">
          <h1 className="text-2xl font-black text-gray-900 tracking-tighter flex items-center gap-2">
            <div className="w-2 h-8 bg-black rounded-full"></div>
            Seller Panel
          </h1>
          <p className="text-[10px] font-black text-green-600 uppercase tracking-[0.2em] mt-2">Admin Dashboard</p>
        </div>

        <div className="px-4 mb-6 mt-4">
          <div className="flex items-center gap-3.5 px-4 py-3 rounded-2xl font-bold text-gray-700 bg-gray-50">
            {shopLogo ? <img src={shopLogo} className="w-8 h-8 rounded-full object-cover" /> : <UserCircle size={28} />}
            <div className="overflow-hidden">
              <p className="text-sm font-black text-gray-900 truncate">{userName}</p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 truncate">{shopName}</p>
            </div>
          </div>
        </div>
        
        {/* Navigation Links */}
        <nav className="px-4 space-y-1.5 overflow-y-auto max-h-[calc(100vh-300px)] no-scrollbar">
          {menuItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => setIsOpen(false)}
              className={`flex items-center gap-3.5 px-4 py-4 rounded-2xl font-bold transition-all duration-200 ${
                location.pathname === item.path 
                ? "bg-black text-white shadow-xl translate-x-1" 
                : "text-gray-400 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <span>{item.icon}</span>
              <span className="text-[14px] uppercase tracking-wider">{item.name}</span>
            </Link>
          ))}
        </nav>

        {/* Logout Area */}
        <div className="absolute bottom-8 left-0 w-full px-4">
          <button onClick={handleLogout} className="w-full flex items-center gap-3.5 px-4 py-4 rounded-2xl font-black text-red-500 hover:bg-red-50 transition-all border border-transparent hover:border-red-100">
            <LogOut size={20} />
            <span className="text-[14px] uppercase tracking-wider">Logout Account</span>
          </button>
        </div>
      </aside>

      {/* --- SCROLLABLE MAIN CONTENT --- */}
      <main className="flex-1 w-full md:ml-72 min-h-screen">
        <div className="p-4 md:p-10 max-w-6xl mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[90] md:hidden" onClick={() => setIsOpen(false)}></div>
      )}
    </div>
  );
};

export default DashboardLayout;